import { useEffect } from "react";

import { usePrevious } from "react-use";
import { useShallow } from "zustand/react/shallow";

import { useConfigureSentryEvents } from "../context/internal/useConfigureSentryEvents";
import { useFetchEnvJson } from "../context/internal/useFetchEnvJson";
import { useWaterlyAppStore } from "../stores";

const normalizePercent = (value: number | null | undefined) => {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return 0;
  }

  return Math.min(100, Math.max(0, value));
};

export const EnvironmentProvider = () => {
  const setEnv = useWaterlyAppStore(useShallow((s) => s.setEnv));
  const setIsNewReleaseAvailable = useWaterlyAppStore(useShallow((s) => s.setIsNewReleaseAvailable));

  const envJson = useFetchEnvJson();

  useConfigureSentryEvents(envJson);

  // Track changes to EnvJson and set up the zustand store accordingly
  useEffect(() => {
    setEnv({
      Deployment: envJson.deployment,
      Release: `frontend-main@${envJson.release}`,
      BuildId: "unknown",
      CommitSHA: "unknown",
      AnalyticsId: envJson.ga_id ?? "unknown",
      AppInsightsConnectionString: envJson.ai,
      MixpanelToken: envJson.mixpanel_token ?? "unknown",
      MixpanelEnableSuperadminTracking: envJson.mixpanel_enable_superadmin_tracking === true,
      MixpanelRecordSessionsPercent: normalizePercent(envJson.mixpanel_record_sessions_percent),
    });
  }, [
    envJson.ai,
    envJson.deployment,
    envJson.ga_id,
    envJson.mixpanel_enable_superadmin_tracking,
    envJson.mixpanel_record_sessions_percent,
    envJson.mixpanel_token,
    envJson.release,
    setEnv,
  ]);

  // Track changes between the previous active release and the current active release and set up the zustand store accordingly
  const previousActiveRelease = usePrevious(envJson?.release);
  useEffect(() => {
    if (!previousActiveRelease) {
      return;
    }

    if (envJson.release !== previousActiveRelease) {
      setIsNewReleaseAvailable(true);
    }
  }, [previousActiveRelease, setIsNewReleaseAvailable, envJson.release]);

  return null;
};
